import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { asyncify, eachLimit } from 'async';
import { cloneDeep, groupBy } from 'lodash';
import { shallow } from 'zustand/shallow';

import { Character, LoadingSpinner } from '../../components/Molecules';
import {
  HandwritingBlankInfo,
  HandwritingGameMode,
  TextOrderingBlankInfo,
  TextOrderingGameMode,
} from '../../components/Organisms';
import { QuestionAnswerLanguageSelectPopup } from '../../components/Popups/QuestionAnswerLanguageSelectPopup';
import { GameMemoryHelperTemplate } from '../../components/Templates/Game';
import { getLanguageDetail } from '../../constants/languageDetails';
import { getCandidateAnswerSlot } from '../../constants/MemoryGames/spellingChar';
import SYALLABIFICATION_JSON_DATA from '../../constants/MemoryGames/syllabification.json';
import { SpacePrivacyPolicy } from '../../constants/space';
import { SVGIconType } from '../../constants/svgIcon';
import { usePopup } from '../../context';
import useAbortControllerSignal from '../../hooks/useAbortControllerSignal';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import { TranslationLocale } from '../../i18n';
import { FileType, TtsLocale } from '../../models/Base';
import { StrokeCharacterData } from '../../models/FileStore';
import {
  FingerWritingAnswer,
  GameType,
  TextOrderingAnswer,
  TextOrderingUnitType,
} from '../../models/GameStore';
import { useFileStore } from '../../stores/FileStore';
import { useGameStore, useGameStoreListField } from '../../stores/GameStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { arrayEqual } from '../../utils/array';
import { getUiLocale } from '../../utils/i18n';
import {
  selectRandomWords,
  splitIntoGraphme,
  splitIntoWords,
} from '../../utils/string';

type State = {
  defaultTextAnswer: TextOrderingAnswer | undefined;
  textAnswer: TextOrderingAnswer | undefined;
  defaultWritingAnswer: FingerWritingAnswer | undefined;
  writingAnswer: FingerWritingAnswer | undefined;
  textBlankInfos: Record<number, TextOrderingBlankInfo> | undefined;
  textGameMode: TextOrderingGameMode | undefined;
  writingBlankInfos: Record<number, HandwritingBlankInfo> | undefined;
  writingGameMode: HandwritingGameMode | undefined;
  strokeDatas: Record<string, StrokeCharacterData> | undefined;
  currentBlankCharacter: Character | null | undefined;
  currentComponent: number;
  currentStrokeIndex: number;
  currentStep: number;
  maximumStep: number;
  defaultStep: number;
  isUseTranslate: boolean;
  tempLanguage: TtsLocale | undefined;
  originalLanguage: TtsLocale | undefined;
  translatedLanguage: TtsLocale | undefined;
  answerText: string;
  originalAudioTts: string | null | undefined;
  translatedAudioTts: string | null | undefined;
  translationResult: string | undefined;
  isLoadingTotal: number;
};

const { fetchAlbum } = useSpaceStore.getState();
const { fetchGameCollectionsByIds, fetchGame } = useGameStore.getState();
const { getTtsAudios, getTranslationText } = useFileStore.getState();

const GameMemoryHelperScreen: React.FC = () => {
  const { i18n } = useTranslation();
  const { showPopup, hidePopup } = usePopup();
  const { classroomId, gameSetId, gameId } = useParams();
  const signal = useAbortControllerSignal();

  const [state, setState] = useState<State>({
    defaultTextAnswer: undefined,
    textAnswer: undefined,
    defaultWritingAnswer: undefined,
    writingAnswer: undefined,
    textBlankInfos: undefined,
    textGameMode: undefined,
    writingBlankInfos: undefined,
    writingGameMode: undefined,
    strokeDatas: undefined,
    currentBlankCharacter: undefined,
    currentComponent: 0,
    currentStrokeIndex: 0,
    currentStep: 0,
    maximumStep: 0,
    defaultStep: 0,
    isUseTranslate: false,
    tempLanguage: undefined,
    originalLanguage: undefined,
    translatedLanguage: undefined,
    answerText: '',
    originalAudioTts: undefined,
    translatedAudioTts: undefined,
    translationResult: undefined,
    isLoadingTotal: 0,
  });

  // MARK: Login Check
  useRedirectEntry();

  const { classroom, classroomMembership } = useSpaceStore((store) => {
    const space = store.getSpace(classroomId ?? '');
    const membership = store.getActiveUserMembership(classroomId ?? '');
    return {
      classroom: space,
      classroomMembership: membership,
    };
  }, shallow);

  // MARK: Club Access Check
  const isAllowToAccessClub =
    (classroom?.privacyPolicy === SpacePrivacyPolicy.Public ||
      (classroom?.privacyPolicy === SpacePrivacyPolicy.PrivateAndVisible &&
        classroomMembership?.isInGroup)) ??
    false;

  useRedirectHome(!isAllowToAccessClub);

  // MARK: Get Album
  const albumListFieldName = `${classroomId}_${gameSetId}`;
  useSpaceStoreListField('albums', albumListFieldName);

  useEffect(() => {
    if (classroom && gameSetId) {
      fetchAlbum({
        fieldName: albumListFieldName,
        classroomId: classroom.classroomId,
        gameSetId,
      })
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, isFinishedFetchAlbum: true }));
        });
    }
  }, [albumListFieldName, classroom, gameSetId]);

  const album = useSpaceStore(
    (store) => store.getAlbum(gameSetId ?? ''),
    shallow,
  );

  // MARK: Get GameCollection
  const gameCollectionFieldName = `album_${album?.gameSetId}`;
  useGameStoreListField('gameCollections', gameCollectionFieldName);

  useEffect(() => {
    if (album?.content?.gameCollectionIds && album?.gameSetId) {
      const gameCollectionIds = album?.content?.gameCollectionIds;
      if (gameCollectionIds) {
        fetchGameCollectionsByIds({
          fieldName: gameCollectionFieldName,
          gameCollectionIds: gameCollectionIds,
        }).catch(console.error);
      }
    }
  }, [
    album?.content?.gameCollectionIds,
    album?.gameSetId,
    gameCollectionFieldName,
  ]);

  const gamesFieldName = `${gameId}`;
  useGameStoreListField('games', gamesFieldName);

  const gameItems = useGameStore((store) => {
    const gameCollections = (album?.content?.gameCollectionIds ?? []).map(
      (id) => store.getGameCollection(id),
    );
    if (
      gameCollections.some((gameCollection) => gameCollection === undefined)
    ) {
      // only return data if all game collection loaded
      return undefined;
    }

    return gameCollections.flatMap(
      (gameCollection) =>
        gameCollection?.getPartItemArray((item) => item.game) ?? [],
    );
  }, arrayEqual(shallow));

  useEffect(() => {
    const game = gameItems?.find(
      (gameItem) => gameItem.game?.gameId === gameId,
    );
    if (game?.game) {
      fetchGame({
        fieldName: gamesFieldName,
        gameCollectionId: game.gameCollectionId,
        gameId: game.game.gameId,
        hash: game.game.hash,
      }).catch(console.error);
    }
  }, [gameId, gameItems, gamesFieldName]);

  const game = useGameStore((store) => {
    return store.getGame(gameId ?? '');
  });

  const getStorkeDatas = useCallback(async () => {
    const result: Record<string, string> = {};
    if (game) {
      await eachLimit(
        game.dependencies.strokes,
        10,
        asyncify(async (fileId: string) => {
          await useFileStore
            .getState()
            .downloadFileUrl({ fileType: FileType.Stroke, fileId }, signal);
          const url = useFileStore
            .getState()
            .getFileDownload(`${FileType.Stroke}_${fileId}`)
            ?.getVersion()?.url;
          if (url) {
            result[fileId] = url;
          }
        }),
      );

      const strokeDatas: Record<string, StrokeCharacterData> = {};

      await eachLimit(
        Object.entries(result),
        10,
        asyncify(async ([id, url]: [string, string]) => {
          const strokeData = await useFileStore
            .getState()
            .getStrokeDataFromUrl(url);
          if (strokeData) {
            strokeDatas[id] = strokeData;
          }
        }),
      );

      setState((s) => ({ ...s, strokeDatas: strokeDatas }));
    }
  }, [game, signal]);

  useEffect(() => {
    getStorkeDatas().catch(console.error);
  }, [getStorkeDatas]);

  useEffect(() => {
    if (game) {
      const locale =
        game.i18nGameData.resources[game.i18nGameData.defaultLocale]
          ?.variants[0]?.answer.locale;
      setState((s) => ({ ...s, originalLanguage: locale }));
    }
  }, [game]);

  useEffect(() => {
    if (game?.gameType === GameType.TextOrdering) {
      const gameAnswer =
        game?.i18nGameData.resources[game?.i18nGameData.defaultLocale]
          ?.variants[0]?.answer;
      if (gameAnswer && !state.defaultTextAnswer) {
        let modifiedAnswer = gameAnswer;
        const originalAnswer = cloneDeep(gameAnswer);
        const splitWords: string[] = [];
        const wordWeights: number[] = [];
        let wordCount = 0;
        originalAnswer.words.forEach((word, i) => {
          word.units.forEach((unit, j) => {
            splitWords.push(`${i}_${j}`);
            wordWeights.push(
              unit.type === TextOrderingUnitType.Candidate
                ? unit.key.length
                : 0,
            );
            if (unit.type === TextOrderingUnitType.Candidate) {
              wordCount += 1;
            }
          });
        });
        if (wordCount > 0) {
          const indexes = selectRandomWords(
            splitWords,
            wordWeights,
            getCandidateAnswerSlot(wordCount, gameAnswer.useColor),
          );

          originalAnswer.words.forEach((word, i) => {
            word.units.forEach((unit, j) => {
              if (
                indexes.find(
                  (selectedIndex) => splitWords[selectedIndex] === `${i}_${j}`,
                ) !== undefined
              ) {
                unit.type = TextOrderingUnitType.Blank;
              } else {
                unit.type = TextOrderingUnitType.Hint;
              }
            });
          });
        }

        originalAnswer.words.forEach((word) => {
          word.units.forEach((unit) => {
            if (unit.type === TextOrderingUnitType.Hint) {
              const isWordLike = splitIntoWords(
                unit.key,
                originalAnswer.locale,
              )[0]?.isWordLike;
              if (isWordLike) {
                unit.type = TextOrderingUnitType.Candidate;
              }
            }
          });
        });

        modifiedAnswer = originalAnswer;

        const words = modifiedAnswer.words.map((word) => word.word);
        const answerText = words.join('');

        let mode: TextOrderingGameMode;
        if (modifiedAnswer.answerFirstLetter) {
          mode = TextOrderingGameMode.QuickMemory;
        } else if (modifiedAnswer.useColor) {
          mode = TextOrderingGameMode.Syllable;
        } else {
          mode = TextOrderingGameMode.Spelling;
        }

        setState((s) => ({
          ...s,
          defaultTextAnswer: modifiedAnswer,
          textAnswer: modifiedAnswer,
          textBlankInfos: {},
          textGameMode: mode,
          answerText: answerText,
        }));
      }
    }
    if (game?.gameType === GameType.FingerWriting) {
      const gameAnswer =
        game?.i18nGameData.resources[game?.i18nGameData.defaultLocale]
          ?.variants[0]?.answer;
      if (gameAnswer && !state.defaultWritingAnswer) {
        const words = gameAnswer.words.map((word) => word.word);
        const answerText = words.join('');
        setState((s) => ({
          ...s,
          defaultWritingAnswer: gameAnswer,
          writingAnswer: gameAnswer,
          writingBlankInfos: {},
          writingGameMode: HandwritingGameMode.QuickHandwriting,
          currentBlankCharacter: null,
          currentStrokeIndex: 0,
          answerText: answerText,
        }));
      }
    }
  }, [
    game?.gameType,
    game?.i18nGameData.defaultLocale,
    game?.i18nGameData.resources,
    state.defaultTextAnswer,
    state.defaultWritingAnswer,
  ]);

  const calculateBlankUnits = useCallback((textAnswer: TextOrderingAnswer) => {
    const units = textAnswer.words.flatMap((word, wordIndex) => {
      // grouping consecutive hints or blanks as an unit group
      // for finding real 'word' (consecutive blanks) in syallable game
      let unitGroup = 0;
      let unitGroupItemIndex = 0; // Track index within the same group
      return word.units.map((unit, unitIndex) => {
        if (unitIndex > 0 && unit.type !== word.units[unitIndex - 1]!.type) {
          unitGroup += 1;
          unitGroupItemIndex = 0; // Reset index for the new group
        }

        const _result = {
          ...unit,
          wordIndex,
          unitIndex,
          unitGroup: `${wordIndex}_${unitGroup}`,
          unitGroupItemIndex,
        };

        if (unit.type === TextOrderingUnitType.Blank) {
          unitGroupItemIndex += 1; // Increment only for blanks
        }

        return _result;
      });
    });

    const blankUnits = units.filter(
      (unit) => unit.type === TextOrderingUnitType.Blank,
    );

    const isWordUnit = units.filter(
      (unit) => unit.type !== TextOrderingUnitType.Hint,
    );

    return { blankUnits, isWordUnit };
  }, []);

  const calculateWordStep = useCallback(
    (answerKey: string, answerLocale: TtsLocale) => {
      const SYALLABIFICATION: Record<string, string> =
        SYALLABIFICATION_JSON_DATA;
      const syllable = SYALLABIFICATION[answerKey.toLowerCase()];

      let syllables: string[];
      if (syllable) {
        syllables = [];

        let syllableStartIndex = 0;
        syllable.split('-').forEach((s) => {
          const originalSyllable = answerKey.substring(
            syllableStartIndex,
            syllableStartIndex + s.length,
          );
          syllableStartIndex += s.length;
          syllables.push(originalSyllable);
        });
      } else {
        // ref: https://stackoverflow.com/a/49407494
        const syllableRegex =
          /[^aeiouy]*[aeiouy]+(?:[^aeiouy]*$|[^aeiouy](?=[^aeiouy]))?/gi;
        syllables = answerKey.match(syllableRegex) ?? [];
      }

      if (
        syllables.length > 1 &&
        state.textGameMode === TextOrderingGameMode.Syllable
      ) {
        return { unit: syllables, wordStep: syllables.length };
      } else {
        const graphmes = splitIntoGraphme(answerKey, answerLocale);
        if (graphmes.length >= 1) {
          return {
            unit: graphmes.map((graphme) => graphme.text),
            wordStep: Math.ceil(graphmes.length / 2),
          };
        }
      }
      return { unit: [], wordStep: 0 };
    },
    [state.textGameMode],
  );

  useEffect(() => {
    if (state.maximumStep === 0) {
      if (state.defaultTextAnswer) {
        const answerLocale = state.defaultTextAnswer.locale;
        const { blankUnits, isWordUnit } = calculateBlankUnits(
          state.defaultTextAnswer,
        );

        if (state.textGameMode === TextOrderingGameMode.QuickMemory) {
          setState((s) => ({
            ...s,
            defaultStep: blankUnits.length + 1,
            currentStep: blankUnits.length + 1,
            maximumStep: isWordUnit.length + 1,
          }));
        } else {
          let defaultStep = 1;
          const newGroupUnits = groupBy(blankUnits, 'unitGroup');
          Object.values(newGroupUnits).forEach((newGroupUnit) => {
            const word = newGroupUnit.map((unit) => unit.key).join('');
            if (newGroupUnit.length === word.length) {
              defaultStep += Math.ceil(newGroupUnit.length / 2);
            } else {
              defaultStep += newGroupUnit.length;
            }
          });

          let maximumStep = defaultStep;
          isWordUnit.forEach((unit) => {
            if (unit.type === TextOrderingUnitType.Candidate) {
              const { wordStep } = calculateWordStep(unit.key, answerLocale);
              maximumStep += wordStep;
            }
          });

          setState((s) => ({
            ...s,
            defaultStep: defaultStep,
            currentStep: defaultStep,
            maximumStep: maximumStep,
          }));
        }
      }
      if (state.defaultWritingAnswer && state.strokeDatas) {
        const strokeDatas = state.strokeDatas;
        const characters = state.defaultWritingAnswer.words
          .flatMap((word) => word.characters)
          .flatMap((character) => {
            const characterData = strokeDatas[character.fileId ?? ''];
            return {
              ...character,
              characterData: characterData
                ? new Character(characterData)
                : null,
            };
          });

        const defaultStep = characters
          .filter((character) => character.strokeCount)
          .reduce((acc, cur) => {
            const strokes = cur.characterData?.strokes;
            if (strokes) {
              if ((strokes[strokes.length - 1]?.component ?? 0) === 0) {
                return acc + Math.ceil(strokes.length / 2);
              } else {
                return acc + (strokes[strokes.length - 1]?.component ?? 0) + 1;
              }
            }
            return acc;
          }, 0);
        const maximumStep = characters
          .filter((character) => character.fileId)
          .reduce((acc, cur) => {
            const strokes = cur.characterData?.strokes;
            if (strokes) {
              if ((strokes[strokes.length - 1]?.component ?? 0) === 0) {
                return acc + Math.ceil(strokes.length / 2);
              } else {
                return acc + (strokes[strokes.length - 1]?.component ?? 0) + 1;
              }
            }
            return acc;
          }, 0);

        setState((s) => ({
          ...s,
          defaultStep: defaultStep + 1,
          currentStep: defaultStep + 1,
          maximumStep: maximumStep + 1,
        }));
      }
    }
  }, [
    calculateBlankUnits,
    calculateWordStep,
    state.defaultTextAnswer,
    state.defaultWritingAnswer,
    state.maximumStep,
    state.strokeDatas,
    state.textGameMode,
  ]);

  const onGetTtsAudio = useCallback(
    async (text: string, locale: TtsLocale, isTranslateAudio: boolean) => {
      if (text) {
        setState((s) => ({ ...s, isLoadingTotal: s.isLoadingTotal + 1 }));
        const audioWithUrl = await getTtsAudios([text], locale, signal);
        setState((s) => ({ ...s, isLoadingTotal: s.isLoadingTotal - 1 }));
        if (audioWithUrl) {
          const url = audioWithUrl[text]?.url;
          if (url) {
            if (isTranslateAudio) {
              setState((s) => ({ ...s, translatedAudioTts: url }));
            } else {
              setState((s) => ({ ...s, originalAudioTts: url }));
            }
          }
        }
      }
    },
    [signal],
  );

  useEffect(() => {
    if (state.answerText && state.originalLanguage) {
      onGetTtsAudio(state.answerText, state.originalLanguage, false).catch(
        console.error,
      );
    }
  }, [onGetTtsAudio, state.answerText, state.originalLanguage]);

  useEffect(() => {
    setState((s) => ({
      ...s,
      translatedLanguage:
        s.translatedLanguage === undefined
          ? getUiLocale(i18n.language as TranslationLocale)
          : s.translatedLanguage,
    }));
  }, [i18n.language]);

  const onSwitchTranslate = useCallback(() => {
    setState((s) => ({ ...s, isUseTranslate: !s.isUseTranslate }));
  }, []);

  const onAutoTranslate = useCallback(async () => {
    if (
      state.answerText &&
      state.isUseTranslate &&
      state.translatedLanguage &&
      state.originalLanguage
    ) {
      setState((s) => ({ ...s, isLoadingTotal: s.isLoadingTotal + 1 }));
      const text = await getTranslationText(
        [state.answerText],
        state.originalLanguage,
        state.translatedLanguage,
      );

      const translationText = text?.join('');

      if (translationText) {
        await onGetTtsAudio(translationText, state.translatedLanguage, true);
      }

      setState((s) => ({ ...s, isLoadingTotal: s.isLoadingTotal - 1 }));
      if (translationText) {
        setState((s) => ({
          ...s,
          translationResult: translationText,
        }));
      }
    }
  }, [
    onGetTtsAudio,
    state.answerText,
    state.isUseTranslate,
    state.originalLanguage,
    state.translatedLanguage,
  ]);

  useEffect(() => {
    if (
      state.isUseTranslate &&
      state.translatedLanguage &&
      state.originalLanguage
    ) {
      onAutoTranslate().catch(console.error);
    }
  }, [
    onAutoTranslate,
    state.isUseTranslate,
    state.originalLanguage,
    state.translatedLanguage,
  ]);

  const handleLanguagePopup = useCallback(
    (selectedLocale: TtsLocale) => {
      showPopup(
        <QuestionAnswerLanguageSelectPopup
          title={'Translation'}
          titleIcon={SVGIconType.Translation}
          languageList={undefined}
          selectedLanguage={selectedLocale}
          recentLanguage={undefined}
          onClickOK={() => {
            setState((s) => ({ ...s, translatedLanguage: s.tempLanguage }));
            hidePopup();
          }}
          onCancel={hidePopup}
          onSelectLanguage={(language: TtsLocale) => {
            setState((s) => ({ ...s, tempLanguage: language }));
            handleLanguagePopup(language); // Re-show popup with updated locale
          }}
        />,
      );
    },
    [hidePopup, showPopup],
  );

  const onClickLanguage = useCallback(() => {
    if (state.translatedLanguage) {
      setState((s) => ({ ...s, tempLanguage: s.translatedLanguage }));
      handleLanguagePopup(state.translatedLanguage);
    }
  }, [handleLanguagePopup, state.translatedLanguage]);

  const questionPanel = useMemo(() => {
    if (state.textAnswer && state.textBlankInfos && state.textGameMode) {
      return {
        textAnswer: state.textAnswer,
        blankInfos: state.textBlankInfos,
        mode: state.textGameMode,
      };
    }
    if (
      state.writingAnswer &&
      state.writingBlankInfos &&
      state.writingGameMode
    ) {
      return {
        writingAnswer: state.writingAnswer,
        blankInfos: state.writingBlankInfos,
        mode: state.writingGameMode,
      };
    }
    return undefined;
  }, [
    state.textAnswer,
    state.textBlankInfos,
    state.textGameMode,
    state.writingAnswer,
    state.writingBlankInfos,
    state.writingGameMode,
  ]);

  const onChangeCurrentStep = useCallback(
    (currentStep: number) => {
      if (currentStep === state.defaultStep) {
        if (state.defaultTextAnswer) {
          setState((s) => ({
            ...s,
            textBlankInfos: {},
            textAnswer: state.defaultTextAnswer,
          }));
        } else if (state.defaultWritingAnswer) {
          setState((s) => ({
            ...s,
            writingBlankInfos: {},
            writingAnswer: state.defaultWritingAnswer,
            currentBlankCharacter: null,
            currentStrokeIndex: 0,
          }));
        }
      }
      if (currentStep < state.defaultStep) {
        if (state.defaultTextAnswer) {
          const { blankUnits } = calculateBlankUnits(state.defaultTextAnswer);
          let stepCount = state.defaultStep - currentStep;
          let blankInfoIndex = 0;
          if (state.textGameMode === TextOrderingGameMode.QuickMemory) {
            blankInfoIndex = stepCount;
          } else {
            const newGroupUnits = groupBy(blankUnits, 'unitGroup');
            Object.values(newGroupUnits).forEach((newGroupUnit) => {
              const word = newGroupUnit.map((unit) => unit.key).join('');
              if (stepCount > 0) {
                if (newGroupUnit.length === word.length) {
                  const addedBlankIndex = Math.min(
                    stepCount * 2,
                    newGroupUnit.length,
                  );
                  blankInfoIndex += addedBlankIndex;
                  stepCount -= Math.ceil(addedBlankIndex / 2);
                } else {
                  const addedBlankIndex = Math.min(
                    stepCount,
                    newGroupUnit.length,
                  );
                  blankInfoIndex += addedBlankIndex;
                  stepCount -= addedBlankIndex;
                }
              }
            });
          }

          const textBlankInfos = Array.from(
            { length: blankInfoIndex },
            (_, index) => index,
          ).reduce(
            (acc, cur) => {
              acc[cur] = { score: 100, pickedAnswer: '' };
              return acc;
            },
            {} as Record<number, TextOrderingBlankInfo>,
          );
          setState((s) => ({
            ...s,
            textBlankInfos: textBlankInfos,
            textAnswer: state.defaultTextAnswer,
          }));
        } else if (state.defaultWritingAnswer && state.strokeDatas) {
          const strokeDatas = state.strokeDatas;
          const characters = state.defaultWritingAnswer.words
            .flatMap((word) => word.characters)
            .flatMap((character) => {
              const characterData = strokeDatas[character.fileId ?? ''];
              return {
                ...character,
                characterData: characterData
                  ? new Character(characterData)
                  : null,
              };
            });
          let stepCount = state.defaultStep - currentStep;
          let blankInfoIndex = 0;
          let remainingComponent = 0;
          let remainingStrokes = 0;
          let currectCharacter: Character | null = null;
          characters
            .filter((character) => character.strokeCount)
            .forEach((character) => {
              const strokes = character.characterData?.strokes;
              if (strokes) {
                const componentCount =
                  (strokes[strokes.length - 1]?.component ?? 0) + 1;

                if (componentCount === 1) {
                  if (stepCount > 0) {
                    const addedBlankIndex = Math.min(
                      stepCount * 2,
                      strokes.length,
                    );
                    blankInfoIndex += 1;
                    stepCount -= Math.ceil(addedBlankIndex / 2);
                    remainingStrokes = addedBlankIndex;
                    remainingComponent = 0;
                    currectCharacter = character.characterData;
                  }
                } else {
                  if (stepCount > 0) {
                    const addedBlankIndex = Math.min(stepCount, componentCount);
                    blankInfoIndex += 1;
                    stepCount -= addedBlankIndex;
                    remainingComponent = addedBlankIndex;
                    remainingStrokes = 0;
                    currectCharacter = character.characterData;
                  }
                }
              }
            });

          const writingBlankInfos = Array.from(
            { length: blankInfoIndex },
            (_, index) => index,
          ).reduce(
            (acc, cur) => {
              acc[cur] = { score: 100, isAnswered: true };
              return acc;
            },
            {} as Record<number, HandwritingBlankInfo>,
          );

          setState((s) => ({
            ...s,
            writingAnswer: state.defaultWritingAnswer,
            writingBlankInfos: writingBlankInfos,
            currentBlankCharacter: currectCharacter,
            currentStrokeIndex: remainingStrokes,
            currentComponent: remainingComponent,
          }));
        }
      }
      if (currentStep > state.defaultStep) {
        if (state.defaultTextAnswer) {
          const answerLocale = state.defaultTextAnswer.locale;
          let stepCount = currentStep - state.defaultStep;
          const textAnswer = cloneDeep(state.defaultTextAnswer);
          textAnswer.words.reverse();
          let blankCount = 0;
          let wordUnitIndex = '';
          let spliceUnits: string[] = [];

          textAnswer.words.forEach((word, wI, words) => {
            if (stepCount > 0) {
              word.units.reverse();
              word.units.forEach((unit, uI, units) => {
                if (
                  stepCount > 0 &&
                  unit.type === TextOrderingUnitType.Candidate
                ) {
                  if (state.textGameMode === TextOrderingGameMode.QuickMemory) {
                    blankCount = 0;
                    wordUnitIndex = `${words.length - 1 - wI}_${
                      units.length - 1 - uI
                    }`;
                    stepCount -= 1;
                    unit.type = TextOrderingUnitType.Blank;
                  } else {
                    const { unit: syllablesUnits, wordStep } =
                      calculateWordStep(unit.key, answerLocale);
                    blankCount =
                      Math.max(wordStep - stepCount, 0) *
                      (syllablesUnits.length === wordStep ? 1 : 2);
                    wordUnitIndex = `${words.length - 1 - wI}_${
                      units.length - 1 - uI
                    }`;
                    stepCount -= wordStep;
                    unit.type = TextOrderingUnitType.Blank;
                    spliceUnits = syllablesUnits;
                  }
                }
              });
              // revert the textAnswer to normal
              word.units.reverse();
            }
          });

          // revert the textAnswer to normal
          textAnswer.words.reverse();

          const [wordIndex, unitIndex] = wordUnitIndex.split('_');
          if (spliceUnits.length > 0) {
            if (wordIndex !== undefined && unitIndex !== undefined) {
              const words = textAnswer.words[Number(wordIndex)];
              words?.units.splice(
                Number(unitIndex),
                1,
                ...spliceUnits.map((unit) => ({
                  key: unit,
                  type: TextOrderingUnitType.Blank,
                })),
              );
            }
          }

          const { blankUnits } = calculateBlankUnits(textAnswer);
          const blankIndex = blankUnits.findIndex(
            (unit) =>
              unit.wordIndex === Number(wordIndex) &&
              unit.unitIndex === Number(unitIndex),
          );

          if (blankIndex > -1) {
            const textBlankInfos = Array.from(
              { length: blankCount },
              (_, index) => index + blankIndex,
            ).reduce(
              (acc, cur) => {
                acc[cur] = { score: 100, pickedAnswer: '' };
                return acc;
              },
              {} as Record<number, TextOrderingBlankInfo>,
            );
            setState((s) => ({
              ...s,
              textAnswer: textAnswer,
              textBlankInfos: textBlankInfos,
            }));
          }
        } else if (state.defaultWritingAnswer && state.strokeDatas) {
          const strokeDatas = state.strokeDatas;
          let stepCount = currentStep - state.defaultStep;
          const writingAnswer = cloneDeep(state.defaultWritingAnswer);
          writingAnswer.words.reverse();

          let blankInfoIndex = 0;
          let remainingStrokes = 0;
          let remainingComponent = 0;
          let currectCharacter: Character | null = null;

          writingAnswer.words.forEach((word) => {
            word.characters.reverse();
            word.characters.forEach((character) => {
              if (stepCount <= 0) {
                if (character.fileId && character.strokeCount) {
                  blankInfoIndex += 1;
                }
              }
              if (stepCount > 0 && !character.strokeCount) {
                const characterData = strokeDatas[character.fileId ?? ''];
                const newCharacterData = characterData
                  ? new Character(characterData)
                  : null;
                const strokes = newCharacterData?.strokes;
                if (strokes) {
                  const componentCount =
                    (strokes[strokes.length - 1]?.component ?? 0) + 1;

                  if (componentCount === 1) {
                    if (stepCount > 0) {
                      const addedBlankIndex = Math.min(
                        stepCount * 2,
                        strokes.length,
                      );
                      stepCount -= Math.ceil(addedBlankIndex / 2);
                      remainingStrokes = addedBlankIndex;
                      remainingComponent = 0;
                      currectCharacter = newCharacterData;
                      character.strokeCount = newCharacterData.strokes.length;
                    }
                  } else {
                    if (stepCount > 0) {
                      const addedBlankIndex = Math.min(
                        stepCount,
                        componentCount,
                      );
                      stepCount -= addedBlankIndex;
                      remainingComponent = addedBlankIndex;
                      remainingStrokes = 0;
                      currectCharacter = newCharacterData;
                      character.strokeCount = newCharacterData.strokes.length;
                    }
                  }
                }
              }
            });
            // revert the writingAnswer to normal
            word.characters.reverse();
          });

          // revert the writingAnswer to normal
          writingAnswer.words.reverse();

          setState((s) => ({
            ...s,
            writingAnswer: writingAnswer,
            writingBlankInfos: {
              [blankInfoIndex]: {
                score: 100,
                isAnswered: true,
              },
            },
            currentBlankCharacter: currectCharacter,
            currentStrokeIndex: remainingStrokes,
            currentComponent: remainingComponent,
          }));
        }
      }
    },
    [
      calculateBlankUnits,
      calculateWordStep,
      state.defaultStep,
      state.defaultTextAnswer,
      state.defaultWritingAnswer,
      state.strokeDatas,
      state.textGameMode,
    ],
  );

  const onClickHideAllStep = useCallback(() => {
    setState((s) => ({ ...s, currentStep: 1 }));
    onChangeCurrentStep(1);
  }, [onChangeCurrentStep]);

  const onClickMinusStep = useCallback(() => {
    setState((s) => ({ ...s, currentStep: state.currentStep - 1 }));
    onChangeCurrentStep(state.currentStep - 1);
  }, [onChangeCurrentStep, state.currentStep]);

  const onClickAddStep = useCallback(() => {
    setState((s) => ({ ...s, currentStep: state.currentStep + 1 }));
    onChangeCurrentStep(state.currentStep + 1);
  }, [onChangeCurrentStep, state.currentStep]);

  const onClickShowAllStep = useCallback(() => {
    setState((s) => ({ ...s, currentStep: state.maximumStep }));
    onChangeCurrentStep(state.maximumStep);
  }, [onChangeCurrentStep, state.maximumStep]);

  return (
    <>
      <GameMemoryHelperTemplate
        questionPanel={questionPanel}
        currentStep={state.currentStep}
        maximumStep={state.maximumStep}
        defaultStep={state.defaultStep}
        isUseTranslate={state.isUseTranslate}
        originalLanguage={
          getLanguageDetail(state.originalLanguage)?.displayName ?? ''
        }
        translatedLanguage={
          getLanguageDetail(state.translatedLanguage)?.displayName ?? ''
        }
        originalAudioTts={state.originalAudioTts}
        translatedAudioTts={state.translatedAudioTts}
        translationResult={state.translationResult}
        currentBlankCharacter={state.currentBlankCharacter}
        currentComponent={state.currentComponent}
        currentStrokeIndex={state.currentStrokeIndex}
        onSwitchTranslate={onSwitchTranslate}
        onClickLanguage={onClickLanguage}
        onClickHideAllStep={onClickHideAllStep}
        onClickMinusStep={onClickMinusStep}
        onClickAddStep={onClickAddStep}
        onClickShowAllStep={onClickShowAllStep}
      />
      {state.isLoadingTotal > 0 && (
        <LoadingSpinner loading={!!state.isLoadingTotal} />
      )}
    </>
  );
};

export default GameMemoryHelperScreen;
